import { Web3Provider } from '@ethersproject/providers'
import { ChainId } from '@uniswap/sdk'
import { useWeb3React as useWeb3ReactCore } from '@web3-react/core'
import { Web3ReactContextInterface } from '@web3-react/core/dist/types'
import { useEffect, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { injected } from '../connectors'
import { NetworkContextName } from '../constants'
import { switchOrAddNetwork, EIP1193Provider } from '../utils/switchOrAddNetwork'

const TARGET_CHAIN_ID = parseInt(process.env.REACT_APP_CHAIN_ID ?? '61900')

// 检查并切换到 Mova 主网络
async function ensureMovaNetwork(provider?: EIP1193Provider | null): Promise<void> {
  const p: EIP1193Provider | undefined =
    provider || (typeof window !== 'undefined' ? ((window as any).ethereum as EIP1193Provider) : undefined)
  if (!p || typeof p.request !== 'function') return

  try {
    // 获取当前链 ID
    const currentChainId = await p.request({ method: 'eth_chainId' })
    const currentChainIdNum = typeof currentChainId === 'string' ? parseInt(currentChainId, 16) : Number(currentChainId)

    // 如果当前不是 Mova 主网，则切换
    if (currentChainIdNum !== TARGET_CHAIN_ID) {
      console.log(`Current chain ID: ${currentChainIdNum}, switching to Mova Mainnet (${TARGET_CHAIN_ID})`)
      await switchOrAddNetwork(p)
    } else {
      console.log('Already on Mova Mainnet')
    }
  } catch (error) {
    console.warn('Failed to check/switch network:', error)
    // 即使检查失败，也尝试切换网络（可能是钱包不支持 eth_chainId）
    await switchOrAddNetwork(p)
  }
}

export function useActiveWeb3React(): Web3ReactContextInterface<Web3Provider> & { chainId?: ChainId } {
  const context = useWeb3ReactCore<Web3Provider>()
  const contextNetwork = useWeb3ReactCore<Web3Provider>(NetworkContextName)
  return context.active ? context : contextNetwork
}

// 检测移动端钱包注入提供者并统一到 window.ethereum
function ensureMobileWalletProvider(): boolean {
  if (typeof window === 'undefined') return false

  // 如果已经有标准的 window.ethereum，直接返回 true
  if (window.ethereum) {
    return true
  }

  // 检查 Bitget Wallet (可能使用 bitkeep.ethereum 或 bitget.ethereum)
  let provider = null
  if ((window as any).bitkeep?.ethereum) {
    provider = (window as any).bitkeep.ethereum
  } else if ((window as any).bitget?.ethereum) {
    provider = (window as any).bitget.ethereum
  } else if ((window as any).web3?.currentProvider) {
    provider = (window as any).web3.currentProvider
  }

  // 如果找到了非标准的提供者，将其赋值给 window.ethereum
  // 这样 InjectedConnector 就能正常工作了
  if (provider) {
    ;(window as any).ethereum = provider
    return true
  }

  return false
}

export function useEagerConnect() {
  const { activate, active, account } = useWeb3ReactCore() // specifically using useWeb3ReactCore because of what this hook does
  const [tried, setTried] = useState(false)

  useEffect(() => {
    injected
      .isAuthorized()
      .then(isAuthorized => {
        console.log('isAuthorized', isAuthorized)
        if (isAuthorized) {
          // 已授权，静默连接前先确保网络正确
          if (window.ethereum) {
            ensureMovaNetwork(window.ethereum as any)
              .then(() => {
                activate(injected, undefined, true).catch(() => {
                  setTried(true)
                })
              })
              .catch(() => {
                // 网络切换失败，仍然尝试连接
                activate(injected, undefined, true).catch(() => {
                  setTried(true)
                })
              })
          } else {
            activate(injected, undefined, true).catch(() => {
              setTried(true)
            })
          }
        } else {
          // 移动端自动连接逻辑
          if (isMobile) {
            // 确保钱包提供者已正确设置到 window.ethereum
            const hasProvider = ensureMobileWalletProvider()
            if (hasProvider && window.ethereum) {
              // 先确保切换到 Mova 主网，再连接
              ensureMovaNetwork(window.ethereum as any)
                .then(() => {
                  // 移动端检测到钱包提供者，尝试自动连接
                  // 注意：这里不使用 silent mode (第三个参数为 undefined)，以便触发授权弹窗
                  // 对于某些钱包（如 Bitget），silent mode 可能无法正常工作
                  activate(injected, undefined, undefined).catch(() => {
                    setTried(true)
                  })
                })
                .catch(() => {
                  // 网络切换失败，仍然尝试连接
                  activate(injected, undefined, undefined).catch(() => {
                    setTried(true)
                  })
                })
            } else {
              setTried(true)
            }
          } else {
            setTried(true)
          }
        }
      })
      .catch(() => {
        // isAuthorized() 失败时，如果是移动端且有钱包提供者，仍然尝试连接
        if (isMobile) {
          const hasProvider = ensureMobileWalletProvider()
          if (hasProvider && window.ethereum) {
            // 先确保切换到 Mova 主网，再连接
            ensureMovaNetwork(window.ethereum as any)
              .then(() => {
                activate(injected, undefined, undefined).catch(() => {
                  setTried(true)
                })
              })
              .catch(() => {
                // 网络切换失败，仍然尝试连接
                activate(injected, undefined, undefined).catch(() => {
                  setTried(true)
                })
              })
          } else {
            setTried(true)
          }
        } else {
          setTried(true)
        }
      })
  }, [activate]) // intentionally only running on mount (make sure it's only mounted once :))

  // if the connection worked, wait until we get confirmation of that to flip the flag
  useEffect(() => {
    if (active) {
      setTried(true)

      // 移动端钱包连接成功但账户为空时，主动请求账户
      // 某些钱包（如 Bitget）可能在连接后不会立即返回账户
      if (isMobile && !account && window.ethereum) {
        const ethereum = window.ethereum as any
        if (ethereum.request) {
          // 延迟一点时间再检查，给钱包一些时间完成连接
          const timer = setTimeout(() => {
            ethereum
              .request({ method: 'eth_accounts' })
              .then((accounts: string[]) => {
                // 如果获取到账户但 account 仍为空，重新激活连接
                if (accounts && accounts.length > 0 && !account) {
                  activate(injected, undefined, true).catch(() => {
                    // 忽略错误，避免循环
                  })
                }
              })
              .catch(() => {
                // 忽略错误
              })
          }, 500) // 延迟 500ms 等待钱包完成初始化

          return () => clearTimeout(timer)
        }
      }
    }
    return undefined
  }, [active, account, activate])

  return tried
}

/**
 * Use for network and injected - logs user in
 * and out after checking what network theyre on
 */
export function useInactiveListener(suppress = false) {
  const { active, error, activate, account } = useWeb3ReactCore() // specifically using useWeb3React because of what this hook does

  useEffect(() => {
    // 确保移动端钱包提供者已正确设置
    if (isMobile) {
      ensureMobileWalletProvider()
    }

    const { ethereum } = window

    if (!ethereum || !ethereum.on) {
      return undefined
    }

    // 移动端特殊处理：即使 active 为 true，如果 account 为空，也要监听事件
    // 某些钱包（如 Bitget）可能在连接后延迟返回账户
    const shouldListen = isMobile ? !error && !suppress : !active && !error && !suppress

    if (!shouldListen) {
      return undefined
    }

    const handleChainChanged = () => {
      // eat errors
      activate(injected, undefined, true).catch(error => {
        console.error('Failed to activate after chain changed', error)
      })
    }

    const handleAccountsChanged = (accounts: string[]) => {
      if (accounts && accounts.length > 0) {
        // eat errors
        activate(injected, undefined, true).catch(error => {
          console.error('Failed to activate after accounts changed', error)
        })
      }
    }

    // 对于移动端钱包，也监听 connect 事件
    const handleConnect = () => {
      // 连接事件触发时，尝试激活
      activate(injected, undefined, true).catch(error => {
        console.error('Failed to activate after connect', error)
      })
    }

    ethereum.on('chainChanged', handleChainChanged)
    ethereum.on('accountsChanged', handleAccountsChanged)

    // 某些钱包（如 Bitget）可能使用 connect 事件
    try {
      ethereum.on('connect', handleConnect)
    } catch (e) {
      // 某些钱包可能不支持 connect 事件，忽略错误
    }

    return () => {
      if (ethereum.removeListener) {
        ethereum.removeListener('chainChanged', handleChainChanged)
        ethereum.removeListener('accountsChanged', handleAccountsChanged)
        try {
          ethereum.removeListener('connect', handleConnect)
        } catch (e) {
          // 忽略错误
        }
      }
    }
  }, [active, error, suppress, activate, account])
}
